import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Clock, Eye, 
  FileText, User, Flag, Sparkles, Brain, RefreshCw,
  ChevronRight, ExternalLink, Mail, Phone, Calendar,
  AlertOctagon, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification Type Display Names
const VERIFICATION_TYPE_NAMES = {
  dbs_basic: 'DBS Basic Check',
  dbs_enhanced: 'DBS Enhanced Check',
  dbs_enhanced_barred: 'DBS Enhanced + Barred List',
  slts_registered: 'SLTS Registration',
  qts: 'Qualified Teacher Status',
  pgce: 'PGCE Certificate',
  moe_certified: 'MOE Certification',
  police_clearance_lk: 'Police Clearance (LK)',
  degree_verified: 'Degree Certificate',
  photo_verified: 'Photo Verification',
  professional_cert: 'Professional Certification',
};

// Stats Card Component
function StatsCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color.replace('text-', 'bg-').replace('600', '100')}`}>
            <Icon className={`w-6 h-6 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Verification Detail Modal
function VerificationDetailModal({ verification, isOpen, onClose, onApprove, onReject, loading }) {
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  
  if (!verification) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Verification Request Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Tutor Info */}
          <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
            <img 
              src={verification.tutor_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(verification.tutor_name || 'T')}&background=6366f1&color=fff`}
              alt={verification.tutor_name}
              className="w-16 h-16 rounded-full object-cover"
            />
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{verification.tutor_name || 'Unknown Tutor'}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  {verification.tutor_email}
                </span>
                <span className="flex items-center gap-1">
                  <Flag className="w-3.5 h-3.5" />
                  {verification.tutor_country || 'UK'}
                </span>
              </div>
            </div>
            <Badge variant={verification.status === 'pending_review' ? 'warning' : verification.status === 'approved' ? 'success' : 'destructive'}>
              {verification.status?.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          
          {/* Document Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Document Type</Label>
              <p className="font-medium">{VERIFICATION_TYPE_NAMES[verification.type] || verification.type}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Submitted</Label>
              <p className="font-medium">
                {verification.submitted_at ? new Date(verification.submitted_at).toLocaleDateString() : 'Unknown'}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">File Name</Label>
              <p className="font-medium text-sm">{verification.file_name || 'No file'}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-500">Verification ID</Label>
              <p className="font-mono text-sm">{verification.id}</p>
            </div>
          </div>
          
          {/* Document Preview */}
          {verification.document_url && (
            <div className="border rounded-lg p-4">
              <Label className="text-gray-500 mb-2 block">Document Preview</Label>
              {verification.document_url.match(/\.(jpg|jpeg|png|webp|gif)$/i) ? (
                <img 
                  src={`${API_URL}${verification.document_url}`}
                  alt="Document"
                  className="max-w-full max-h-96 rounded border"
                />
              ) : (
                <div className="flex items-center justify-center p-8 bg-gray-100 rounded">
                  <a 
                    href={`${API_URL}${verification.document_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-indigo-600 hover:underline"
                  >
                    <FileText className="w-5 h-5" />
                    View Document
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              )}
            </div>
          )}
          
          {/* Fraud & Safety Indicators */}
          <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
            <h4 className="font-medium flex items-center gap-2 text-amber-800 mb-3">
              <AlertOctagon className="w-4 h-4" />
              Fraud & Safety Check
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No duplicate submissions</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Valid file format</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No prior complaints</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Document not expired</span>
              </div>
            </div>
          </div>
          
          {/* Admin Notes */}
          {verification.status === 'pending_review' && !showRejectForm && (
            <div>
              <Label htmlFor="notes">Admin Notes (Optional)</Label>
              <Textarea 
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any notes about this verification..."
                rows={2}
              />
            </div>
          )}
          
          {/* Reject Form */}
          {showRejectForm && (
            <div className="border border-red-200 rounded-lg p-4 bg-red-50">
              <Label className="text-red-700">Rejection Reason *</Label>
              <Textarea 
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Please provide a clear reason for rejection..."
                rows={3}
                className="mt-2"
              />
              <div className="flex gap-2 mt-3">
                <Button 
                  variant="ghost" 
                  onClick={() => setShowRejectForm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => onReject(verification.id, rejectReason)}
                  disabled={!rejectReason.trim() || loading}
                >
                  {loading ? 'Rejecting...' : 'Confirm Rejection'}
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {verification.status === 'pending_review' && !showRejectForm && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
            <Button 
              variant="destructive"
              onClick={() => setShowRejectForm(true)}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
            <Button 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onApprove(verification.id, notes)}
              disabled={loading}
            >
              {loading ? 'Approving...' : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function AdminTutorVerifications() {
  const [activeTab, setActiveTab] = useState('pending');
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // AI Auto-Approval Settings
  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    autoApprovePhotos: true,
    autoApproveLowRisk: true,
    confidenceThreshold: 0.85,
  });
  
  // Complaints (mock data for now)
  const [complaints, setComplaints] = useState([
    { id: '1', tutor_name: 'John Smith', type: 'inappropriate_content', status: 'open', created_at: '2026-02-01' },
    { id: '2', tutor_name: 'Sarah Jones', type: 'fake_qualifications', status: 'investigating', created_at: '2026-01-30' },
  ]);
  
  // Fetch verifications
  const fetchVerifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/education-tuition/admin/verifications/pending?limit=50`);
      const data = await response.json();
      
      if (data.success) {
        setVerifications(data.verifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch verifications:', error);
      toast.error('Failed to fetch verifications');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/education-tuition/admin/verifications/stats`);
      const data = await response.json();
      
      if (data.success) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, []);
  
  useEffect(() => {
    fetchVerifications();
    fetchStats();
  }, [fetchVerifications, fetchStats]);
  
  // Approve verification
  const handleApprove = async (verificationId, notes) => {
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/education-tuition/admin/verifications/${verificationId}/approve?notes=${encodeURIComponent(notes || '')}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success('Verification approved');
        setShowDetailModal(false);
        fetchVerifications();
        fetchStats();
      } else {
        toast.error(data.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error('Failed to approve verification');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Reject verification
  const handleReject = async (verificationId, reason) => {
    if (!reason?.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setActionLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/education-tuition/admin/verifications/${verificationId}/reject?reason=${encodeURIComponent(reason)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      if (data.success) {
        toast.success('Verification rejected');
        setShowDetailModal(false);
        fetchVerifications();
        fetchStats();
      } else {
        toast.error(data.detail || 'Failed to reject');
      }
    } catch (error) {
      toast.error('Failed to reject verification');
    } finally {
      setActionLoading(false);
    }
  };
  
  // Quick approve
  const handleQuickApprove = async (verification) => {
    if (window.confirm(`Approve ${VERIFICATION_TYPE_NAMES[verification.type]} for ${verification.tutor_name}?`)) {
      await handleApprove(verification.id, 'Quick approval');
    }
  };
  
  // Quick reject
  const handleQuickReject = async (verification) => {
    const reason = window.prompt('Enter rejection reason:');
    if (reason) {
      await handleReject(verification.id, reason);
    }
  };
  
  // Open detail modal
  const openDetail = (verification) => {
    setSelectedVerification(verification);
    setShowDetailModal(true);
  };
  
  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-indigo-600" />
              Tutor Verification Admin
            </h1>
            <p className="text-gray-500 mt-1">Review and approve tutor verification documents</p>
          </div>
          
          <Button onClick={() => { fetchVerifications(); fetchStats(); }} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatsCard 
            title="Pending Reviews" 
            value={stats?.pending_verifications || 0}
            icon={Clock}
            color="text-amber-600"
            subtitle="Awaiting decision"
          />
          <StatsCard 
            title="Approved" 
            value={stats?.approved_verifications || 0}
            icon={CheckCircle}
            color="text-green-600"
            subtitle="This month"
          />
          <StatsCard 
            title="Rejected" 
            value={stats?.rejected_verifications || 0}
            icon={XCircle}
            color="text-red-600"
            subtitle="This month"
          />
          <StatsCard 
            title="Active Tutors" 
            value={stats?.active_tutors || 0}
            icon={User}
            color="text-indigo-600"
            subtitle="Verified & active"
          />
        </div>
        
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Pending ({verifications.length})
            </TabsTrigger>
            <TabsTrigger value="ai_settings" className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              AI Settings
            </TabsTrigger>
            <TabsTrigger value="complaints" className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Complaints ({complaints.length})
            </TabsTrigger>
            <TabsTrigger value="fraud" className="flex items-center gap-2">
              <AlertOctagon className="w-4 h-4" />
              Fraud & Safety
            </TabsTrigger>
          </TabsList>
          
          {/* Pending Verifications Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-indigo-600" />
                  Pending Verification Requests
                </CardTitle>
                <CardDescription>
                  Review documents submitted by tutors for verification
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="h-16 bg-gray-100 rounded animate-pulse" />
                    ))}
                  </div>
                ) : verifications.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <p className="text-gray-500">No pending verifications</p>
                    <p className="text-sm text-gray-400">All caught up!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tutor</TableHead>
                        <TableHead>Document Type</TableHead>
                        <TableHead>Country</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {verifications.map(verification => (
                        <TableRow key={verification.id} data-testid={`verification-row-${verification.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <img 
                                src={verification.tutor_photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(verification.tutor_name || 'T')}&background=6366f1&color=fff&size=32`}
                                alt={verification.tutor_name}
                                className="w-8 h-8 rounded-full"
                              />
                              <div>
                                <p className="font-medium">{verification.tutor_name || 'Unknown'}</p>
                                <p className="text-xs text-gray-500">{verification.tutor_email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {VERIFICATION_TYPE_NAMES[verification.type] || verification.type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm">
                              {verification.tutor_country === 'LK' ? '🇱🇰 Sri Lanka' : '🇬🇧 UK'}
                            </span>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-gray-500">
                              {verification.submitted_at 
                                ? new Date(verification.submitted_at).toLocaleDateString()
                                : 'Unknown'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openDetail(verification)}
                                data-testid={`view-btn-${verification.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleQuickApprove(verification)}
                                data-testid={`approve-btn-${verification.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleQuickReject(verification)}
                                data-testid={`reject-btn-${verification.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* AI Settings Tab */}
          <TabsContent value="ai_settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                  AI Auto-Approval Settings
                </CardTitle>
                <CardDescription>
                  Configure AI-powered automatic verification approval
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Master Toggle */}
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <Brain className="w-8 h-8 text-purple-600" />
                    <div>
                      <h4 className="font-medium">AI Auto-Approval</h4>
                      <p className="text-sm text-gray-600">
                        Automatically approve low-risk verifications
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={aiSettings.enabled}
                    onCheckedChange={(v) => setAiSettings(prev => ({ ...prev, enabled: v }))}
                    data-testid="ai-enabled-switch"
                  />
                </div>
                
                {aiSettings.enabled && (
                  <div className="space-y-4 pl-4 border-l-2 border-purple-200">
                    {/* Auto-approve photos */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-approve Profile Photos</Label>
                        <p className="text-sm text-gray-500">
                          Automatically verify clear profile photos
                        </p>
                      </div>
                      <Switch 
                        checked={aiSettings.autoApprovePhotos}
                        onCheckedChange={(v) => setAiSettings(prev => ({ ...prev, autoApprovePhotos: v }))}
                      />
                    </div>
                    
                    {/* Auto-approve low risk */}
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Auto-approve Low-Risk Documents</Label>
                        <p className="text-sm text-gray-500">
                          Auto-approve when AI confidence is high
                        </p>
                      </div>
                      <Switch 
                        checked={aiSettings.autoApproveLowRisk}
                        onCheckedChange={(v) => setAiSettings(prev => ({ ...prev, autoApproveLowRisk: v }))}
                      />
                    </div>
                    
                    {/* Confidence threshold */}
                    <div>
                      <Label>AI Confidence Threshold</Label>
                      <p className="text-sm text-gray-500 mb-2">
                        Minimum confidence score for auto-approval: {Math.round(aiSettings.confidenceThreshold * 100)}%
                      </p>
                      <input 
                        type="range"
                        min="0.5"
                        max="0.99"
                        step="0.01"
                        value={aiSettings.confidenceThreshold}
                        onChange={(e) => setAiSettings(prev => ({ ...prev, confidenceThreshold: parseFloat(e.target.value) }))}
                        className="w-full"
                      />
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <Button onClick={() => toast.success('AI settings saved!')}>
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Complaints Tab */}
          <TabsContent value="complaints">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="w-5 h-5 text-red-600" />
                  Child Safety & Complaints
                </CardTitle>
                <CardDescription>
                  Review complaints related to tutor conduct and child safety
                </CardDescription>
              </CardHeader>
              <CardContent>
                {complaints.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-3" />
                    <p className="text-gray-500">No open complaints</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tutor</TableHead>
                        <TableHead>Complaint Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Reported</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {complaints.map(complaint => (
                        <TableRow key={complaint.id}>
                          <TableCell className="font-medium">{complaint.tutor_name}</TableCell>
                          <TableCell>
                            <Badge variant={complaint.type === 'fake_qualifications' ? 'warning' : 'destructive'}>
                              {complaint.type.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={complaint.status === 'open' ? 'destructive' : 'warning'}>
                              {complaint.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-gray-500">
                            {new Date(complaint.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              <Eye className="w-4 h-4 mr-1" /> Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Fraud & Safety Tab */}
          <TabsContent value="fraud">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertOctagon className="w-5 h-5 text-amber-600" />
                    Fraud Detection & Child Safety
                  </CardTitle>
                  <CardDescription>
                    Automated checks for fraud prevention and child protection
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h4 className="font-medium text-green-800">Active Checks</h4>
                      </div>
                      <ul className="text-sm text-green-700 space-y-1">
                        <li>• Duplicate document detection</li>
                        <li>• Face matching verification</li>
                        <li>• Document authenticity check</li>
                        <li>• Blacklist database check</li>
                      </ul>
                    </div>
                    
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <h4 className="font-medium text-amber-800">Alert Triggers</h4>
                      </div>
                      <ul className="text-sm text-amber-700 space-y-1">
                        <li>• Multiple failed verifications</li>
                        <li>• Expired DBS/Police clearance</li>
                        <li>• Flagged by other platforms</li>
                        <li>• Parent/Student complaints</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h4 className="font-medium text-indigo-800 mb-2">Child Protection Compliance</h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>UK DBS Integration</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>LK Police Clearance</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>SLTS Registry Check</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Photo Verification</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Qualification Check</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span>Annual Re-verification</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Detail Modal */}
      <VerificationDetailModal 
        verification={selectedVerification}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        onApprove={handleApprove}
        onReject={handleReject}
        loading={actionLoading}
      />
    </div>
  );
}
